(() => {
  class SearchMenu {
    constructor() {
      this.$autocompleteContainer = document.querySelector('[data-autocomplete]');
      this.$searchInput = document.querySelector('[data-search-menu-input]');
      this.$searchMenu = document.querySelector('[data-search-menu-container]');
      this.$searchOpen = document.querySelector('[data-search-menu-open]');
      this.accountId = window.nostoSettings.accountId;
      this.commaDecimalCurrencies = ['EUR'];
      this.desktopBp = window.matchMedia('(min-width: 1024px)');
      this.nostoGQLEndpoint = 'https://search.nosto.com/v1/graphql';
      this.nostoKey = window.nostoSettings.key;
      this.nostoGQLHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.nostoKey}`
      };

      this.events();
    }

    focusTrap() {
      trapFocus(this.$searchMenu);
    }

    focusUntrap() {
      window.removeTrapFocus();
    }

    openMobileSearchMenu() {
      if (this.desktopBp.matches) return;
      document.documentElement.classList.add('mobile-menu--search-active');
      requestAnimationFrame(() => {
        trapFocus(this.$searchMenu);
      });
    }

    events() {
      if (this.$searchInput) {
        this.$searchInput.addEventListener('click', () => {
          this.openMobileSearchMenu();
        });

        this.$searchInput.addEventListener('input', () => {
          const $autocomplete = document.querySelector('.autocomplete');
          if ($autocomplete) {
            $autocomplete.classList.toggle('autocomplete--has-query', this.$searchInput.value.length > 0);
          }
        });

        this.$searchInput.addEventListener('input', debounce(() => {
          this.makeQuery();
          this.renderSidebar();
          this.openMobileSearchMenu();
        }, 200));
      }

      this.$searchOpen.addEventListener('click', e => {
        if (this.desktopBp.matches) return;
        publish(PUB_SUB_EVENTS.mobileSearchOpen, {
          e: e,
          source: 'mobile-search-open-el',
        });

        document.documentElement.classList.add('mobile-menu--search-active');
        setTimeout(() => {
          trapFocus(this.$searchMenu);
        }, 600);
      });

      document.addEventListener('click', e => {
        const $open = e.target.closest('[data-search-menu-open]');
        if ($open) {
          e.preventDefault();
          this.searchMenuOpen();
        }

        const $close = e.target.closest('[data-search-menu-close]');
        if ($close) {
          e.preventDefault();
          this.searchMenuClose();
        }
      });

      document.addEventListener('onEsc', () => {
        if (!document.documentElement.classList.contains('search--active')) return;
        this.searchMenuClose();
      });
    }

    searchMenuClose() {
      document.documentElement.classList.remove('search--active');

      this.focusUntrap();

      setTimeout(() => {
        if (this.desktopBp.matches) this.$searchMenu.style.display = 'none';
        this.$searchOpen.focus();

        document.body.style.position = null;
        document.body.style.left = null;
        document.body.style.right = null;
        document.body.style.top = null;
        window.scrollTo(0, window.searchMenuOpenScrollTop);

        requestAnimationFrame(() => {
          window.menuToggling = false;
        });
      }, 250);
    }

    searchMenuOpen() {
      window.menuToggling = true;

      this.$searchMenu.style.display = 'block';

      this.focusTrap();

      window.searchMenuOpenScrollTop = window.pageYOffset;
      document.body.style.left = 0;
      document.body.style.right = 'var(--scrollbar-width)';
      document.body.style.top = `-${window.searchMenuOpenScrollTop}px`;
      document.body.style.position = 'fixed';

      requestAnimationFrame(() => {
        document.documentElement.classList.add('search--active');
        this.$searchInput.focus();
      });
    }

    getActiveFilters() {
      const activeFilters = [];

      if (window.nostoSettings.searchMenuHideUnavailable) {
        const obj = {};
        obj.field = 'availability';
        obj.value = ['InStock'];
        activeFilters.push(obj);
      }

      return activeFilters;
    }

    makeQuery() {
      const queryString = this.$searchInput.value;
      const nostoQuery = `
        query($queryString: String, $filters: [InputSearchTopLevelFilter!], $sortOrder: [InputSearchSort!]) {
          search(
            accountId: "${this.accountId}",
            query: $queryString,
            products: {
              size: 48,
              filter: $filters,
              sort: $sortOrder
            },
            keywords: {
              size: 5
            }
          ) {
            products {
              hits {
                url
                productId
                name
                imageUrl
                thumbUrl
                brand
                availability
                price
                listPrice
                categoryIds
                inventoryLevel
                tags1
                tags2
                tags3
                priceCurrencyCode
                alternateImageUrls
                skus {
                  id
                  name
                  price
                  url
                  imageUrl
                  availability
                  customFields {
                    key
                    value
                  }
                }
                customFields {
                  key
                  value
                }
              }
              total
              size
            }
            keywords {
              hits {
                keyword
                _redirect
                _highlight {
                  keyword
                }
              }
            }
            query
          }
        }
      `;

      fetch(this.nostoGQLEndpoint, {
        body: JSON.stringify({
          'query': nostoQuery,
          'variables': {
            'queryString': queryString,
            'filters': this.getActiveFilters(),
            'sortOrder': [],
          }
        }),
        headers: this.nostoGQLHeaders,
        method: 'POST',
      }).then(result => result.json()).then(result => {
        if (result.errors) {
          console.error('Nosto GraphQL errors:', result.errors);
        }

        if (!result.data || !result.data.search) {
          console.error('Nosto search returned no data');
          return;
        }

        try {
          this.sendNostoAnalytics(result, queryString);
        } catch (e) {
          console.error(e);
        }

        if (!this.$autocompleteContainer) return;
        const searchData = result.data.search;
        const productData = searchData.products;
        const keywordsData = searchData.keywords;
        this.$autocompleteContainer.innerHTML = '';

        this.renderKeywords(keywordsData);

        GlobaleApp.prototype.modules.priceInfo.load().then(() => {
          productData.hits.forEach((product, index) => index <= 3 && this.renderSearchProduct(product));
          if (productData.hits.length > 0) {
            this.renderViewAll(queryString, productData.total);
          } else {
            this.viewAll = '';
          }
        });
      });
    }

    renderViewAll(queryString, total) {
      this.viewAll = document.createElement('a');
      this.viewAll.classList = 'autocomplete__view-all';
      this.viewAll.href = `${window.routes.root_url}pages/search-results-page?q=${queryString}`;
      this.viewAll.innerText = `View all ${total} products`;
      this.$autocompleteContainer.append(this.viewAll);
    }

    renderKeywords(keywordsData) {
      let $keywordsContainer = document.querySelector('#nostoKeywords');

      if (!$keywordsContainer) {
        const $autocompleteInner = document.querySelector('.autocomplete__inner');

        if ($autocompleteInner) {
          $keywordsContainer = document.createElement('div');
          $keywordsContainer.id = 'nostoKeywords';
          $keywordsContainer.classList.add('autocomplete__links', 'autocomplete__links--nosto-keywords');

          let $predictiveSearch = document.querySelector('#predictiveSearch');
          if (!$predictiveSearch) {
            $predictiveSearch = document.createElement('div');
            $predictiveSearch.id = 'predictiveSearch';
            $predictiveSearch.classList.add('autocomplete__links', 'autocomplete__links--dynamic');
          }

          const $staticLinks = document.querySelector('.autocomplete__links--static');
          if ($staticLinks) {
            $staticLinks.parentNode.insertBefore($keywordsContainer, $staticLinks);
            $staticLinks.parentNode.insertBefore($predictiveSearch, $staticLinks);
          } else {
            $autocompleteInner.insertBefore($predictiveSearch, $autocompleteInner.firstChild);
            $autocompleteInner.insertBefore($keywordsContainer, $autocompleteInner.firstChild);
          }

          $autocompleteInner.classList.add('autocomplete__inner--column');
        }
      }

      if (!$keywordsContainer) return;

      $keywordsContainer.innerHTML = '';

      if (!keywordsData || !keywordsData.hits || keywordsData.hits.length === 0) {
        return;
      }

      const keywordsContainer = document.createElement('div');
      keywordsContainer.classList.add('autocomplete__keywords');

      const keywordsTitle = document.createElement('strong');
      keywordsTitle.classList.add('autocomplete__title', 'block');
      keywordsTitle.innerText = 'Suggestions';
      keywordsContainer.appendChild(keywordsTitle);

      const keywordsList = document.createElement('div');
      keywordsList.classList.add('autocomplete__links-list', 'autocomplete__keywords-list');

      keywordsData.hits.forEach(hit => {
        const keywordLink = document.createElement('a');
        keywordLink.classList.add('autocomplete__link');

        if (hit._redirect) {
          keywordLink.href = hit._redirect;
        } else {
          keywordLink.href = `${window.routes.root_url}pages/search-results-page?q=${encodeURIComponent(hit.keyword)}`;
        }

        if (hit._highlight && hit._highlight.keyword) {
          keywordLink.innerHTML = hit._highlight.keyword;
        } else {
          keywordLink.textContent = hit.keyword;
        }

        keywordLink.addEventListener('click', e => {
          try {
            this.sendNostoKeywordClickAnalytics(e, hit.keyword);
          } catch (error) {
            console.error(error);
          }
        });

        keywordsList.appendChild(keywordLink);
      });

      keywordsContainer.appendChild(keywordsList);
      $keywordsContainer.appendChild(keywordsContainer);
    }

    renderSearchProduct(product) {
      let productPrice = `${new Intl.NumberFormat('en-US', { style: 'currency', currency: window.Shopify.currency.active, currencyDisplay: 'narrowSymbol' }).format(GlobaleApp.prototype.modules.priceInfo.convertPrice(product.price))}${Shopify.currency.active !== 'USD' ? ` ${Shopify.currency.active}` : ''}`;
      let compareAtPrice = new Intl.NumberFormat('en-US', { style: 'currency', currency: window.Shopify.currency.active, currencyDisplay: 'narrowSymbol' }).format(GlobaleApp.prototype.modules.priceInfo.convertPrice(product.listPrice) || 0);

      if (this.commaDecimalCurrencies.includes(window.Shopify.currency.active)) {
        productPrice = productPrice.replace('.', ',').replace('.00', '');
        compareAtPrice = `${compareAtPrice.replace('.', ',').replace('.00', '')}${Shopify.currency.active !== 'USD' ? ` ${Shopify.currency.active}` : ''}`;
      }

      const price = product.listPrice > product.price ? `<s aria-label="Was ${compareAtPrice}">${compareAtPrice}</s> <span>${productPrice}</span>` : productPrice;
      const titleArray = product.name.split(' - ');
      const title = titleArray.length > 0 ? titleArray[0] : product.name;
      const subtitle = titleArray.length < 1 ? null : `<div class="subline block color-theme__background-color color-dim letter-spacing margin product-card__subtitle" style="--margin-desktop: 4px 0 0; --margin-mobile: 2px 0 0; line-height: normal;">${product.name.split(' - ')[1]}</div>`;
      const template = `
        <a href="${product.url}" class="autocomplete__item">
          <figure class="autocomplete__image-holder">
            <img srcset="${imgURL(product.thumbUrl, '200x270', true)}, ${imgURL(product.thumbUrl, '400x540', true)}"
              src="${imgURL(product.thumbUrl, '100x')}"
              class="autocomplete__image"
              alt=""
              aria-hidden="true"
              role="presentation"
              height="135"
              width="100"
            >
          </figure>

          <div class="product-card__title block color-theme__background-color color-theme__color no-u tt--upper" style="font-size: 14px;">
            ${title}
          </div>
          ${subtitle && subtitle}
          <span class="autocomplete__price product-card__price color-theme__background-color color-theme__color font-size letter-spacing tt--upper"
            style="
              --font-size-scale: 12;
              --font-size-desktop: 12px;
              --font-size-mobile: 12px;
              line-height: 1;
            ">
            ${price}
          </span>
        </a>
      `;

      if (this.$autocompleteContainer) {
        const newEl = this.createElementFromHTML(template);

        newEl.addEventListener('click', e => {
          try {
            this.sendNostoClickAnalytics(e, product.productId);
          } catch (error) {
            console.error(error);
          }
        });

        this.$autocompleteContainer.appendChild(newEl);
      }
    }

    renderSidebar() {
      const $pagesContainer = document.querySelector('#predictiveSearch');

      if (!$pagesContainer) return;

      if (this.$searchInput.value === '') {
        $pagesContainer.innerHTML = '';
        return;
      }

      this.requestResponse = null;

      fetch(`${Shopify.routes.root}search/suggest?q=${this.$searchInput.value}&section_id=predictive-search`)
        .then((response) => {
          this.requestResponse = response;
          return response.text();
        })
        .then((text) => {
          if (!(this.requestResponse && this.requestResponse.ok)) {
            throw new Error(`${this.requestResponse.status}: ${text}`);
          }

          const parsedDoc = new DOMParser().parseFromString(text, 'text/html');
          const parsedEl = parsedDoc.getElementById('predictiveSearch');

          if (!parsedEl) return;

          const titles = parsedEl.querySelectorAll('.autocomplete__title');
          titles.forEach(title => {
            const text = title.textContent.toLowerCase().trim();
            if (text === 'suggestions' || text.includes('suggestion')) {
              const nextSibling = title.nextElementSibling;
              if (nextSibling && nextSibling.classList.contains('autocomplete__links-list')) {
                nextSibling.remove();
              }
              title.remove();
            }
          });

          $pagesContainer.innerHTML = parsedEl.innerHTML;
        })
        .catch((error) => {
          console.error(error);
        });
    }

    createElementFromHTML(htmlString) {
      const div = document.createElement('div');
      div.innerHTML = htmlString.trim();
      return div.firstChild;
    }

    sendNostoAnalytics(response, queryString) {
      const type = 'autocomplete';
      const query = {
        query: queryString,
        sort: [],
        filters: []
      };

      const searchResult = response.data.search;
      const options = {
        isKeyword: false
      };

      if (!window.nostojs) {
        (function() {
          const name = 'nostojs';
          window[name] = window[name] || function(cb) {
            (window[name].q = window[name].q || []).push(cb);
          }
        })()
      }

      window.nostojs(api => {
        api.recordSearch(
          type,
          query,
          searchResult,
          options
        );
      });
    }

    sendNostoClickAnalytics(event, productId) {
      const target = event.target.closest('a')
      if (target) {
        event.preventDefault()
        window.nostojs(api => {
          const type = 'autocomplete'
          const url = target.href
          const hit = {
            productId: productId,
            url: url
          }
          api.recordSearchClick(type, hit)
        })
        window.location.href = target.href
      }
    }

    sendNostoKeywordClickAnalytics(event, keyword) {
      const target = event.target.closest('a')
      if (target) {
        event.preventDefault()
        window.nostojs(api => {
          const type = 'autocomplete'
          const hit = {
            keyword: keyword,
            url: target.href
          }
          api.recordSearchClick(type, hit)
        })
        window.location.href = target.href
      }
    }
  }

  if (document.querySelector('[data-search-menu-form]')) {
    new SearchMenu();
  }
})();
